import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Logo, Disposal32 } from '@packages/themes/icons';
import { useEffect, useMemo, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { TextField, Box, Typography, LoginButton, FlatIconButton } from '@packages/shared';
import { useTracking } from '@packages/tracking';
import * as zod from 'zod';
import type { CaptchaData, GenericSuccessResult } from './types';

const messages = defineMessages({
  captcha: {
    id: 'Captcha.Description',
    defaultMessage:
      'Aus Sicherheitsgründen bitten wir Sie darum, den unten abgebildeten Code einzugeben. So schützen Sie Ihre Anfrage vor möglichem Missbrauch.',
  },
  errorMandatoryField: {
    id: 'Login.message.mandatoryField',
    defaultMessage: 'Pflichtfeld',
  },
});

interface CaptchaProps {
  captchaData: CaptchaData;
  onSuccess: () => void;
}

interface FormProps {
  captcha: string;
}

export const Captcha = ({
  captchaData: { imageUrl, actionUrl },
  onSuccess,
}: CaptchaProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const [captchaImageUrl, setCaptchaImageUrl] = useState<string>(imageUrl);
  const dispatch = useTracking();

  const schema = useMemo(
    () =>
      zod.object({
        captcha: zod.string({
          required_error: formatMessage(messages.errorMandatoryField),
        }),
      }),
    [formatMessage],
  );

  const onSubmit = async ({ captcha }: FormProps) => {
    await fetch(actionUrl, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: `captcha_code=${encodeURIComponent(captcha)}`,
    }).then(async (response) => {
      if (response.ok) {
        const json = (await response.json()) as GenericSuccessResult;
        if (!json.success) {
          setCaptchaImageUrl(`${imageUrl}?ts=${new Date().getTime()}`);
        } else {
          onSuccess();
        }
      }
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
  } = useForm<FormProps>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  useEffect(() => {
    dispatch({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'captcha',
    });
  }, [dispatch]);

  return (
    <Box sx={{ px: [2, 3, 5.5], py: 2 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: 125,
          }}
        >
          <Logo
            sx={{
              height: 48,
              width: '100%',
              mb: 2,
            }}
          />
        </Box>
        <Typography sx={{ mb: 2 }}>{formatMessage(messages.captcha)}</Typography>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          {/* Catpcha image is generated each call and cannot be optimized */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img data-testid="captcha-image" src={captchaImageUrl} alt="captcha" />
          <FlatIconButton
            data-testid="reload-image"
            onClick={() => setCaptchaImageUrl(`${imageUrl}?ts=${new Date().getTime()}`)}
          >
            <Disposal32 />
          </FlatIconButton>
        </Box>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <TextField
            sx={{ pt: 0 }}
            {...register('captcha')}
            error={touchedFields.captcha && !!errors.captcha?.message}
            helperText={touchedFields.captcha ? errors.captcha?.message : undefined}
          />
          <LoginButton
            sx={{
              ml: 1,
            }}
            type="submit"
            data-testid="captcha-submit"
          />
        </Box>
      </form>
    </Box>
  );
};
